(() => {
  // Custom elements initialise again when Shopify replaces a section in the editor.
  if (!customElements.get('fuel-hq-articles')) {
    customElements.define('fuel-hq-articles', class extends HTMLElement {
      connectedCallback() {
        if (!this.initialised) {
          this.initialised = true;
          this.nextPage = 1;
          this.seen = new Set();
          this.grid = this.querySelector('[data-article-grid]');
          this.section = this.querySelector('[data-article-section]');
          this.more = this.querySelector('[data-article-more]');
          this.status = this.querySelector('[data-article-status]');
          this.note = this.querySelector('[data-article-editor-note]');
          this.more.addEventListener('click', () => this.loadMore(true));
        }
        if (!this.seen.size && this.nextPage !== null) this.loadMore();
      }

      disconnectedCallback() {
        this.controller?.abort();
        this.loading = false;
      }

      articleCard(article) {
        const url = new URL(article.url, location.origin);
        if (url.origin !== location.origin || !url.pathname.includes('/blogs/')) return null;
        const card = document.createElement('article');
        card.className = 'fuel-hq__article fuel-hq__panel';
        const pictureLink = document.createElement('a');
        pictureLink.href = url.href;
        pictureLink.tabIndex = -1;
        pictureLink.setAttribute('aria-hidden', 'true');
        const fallback = () => {
          const placeholder = document.createElement('div');
          placeholder.className = 'fuel-hq__article-image fuel-hq__article-image--empty';
          placeholder.textContent = 'Fuel HQ';
          pictureLink.replaceChildren(placeholder);
        };
        if (article.image) {
          const imageUrl = new URL(article.image, location.origin);
          if (['https:', 'http:'].includes(imageUrl.protocol)) {
            const picture = document.createElement('img');
            picture.className = 'fuel-hq__article-image';
            picture.src = imageUrl.href;
            picture.alt = '';
            picture.width = 900;
            picture.height = 563;
            picture.loading = 'lazy';
            picture.addEventListener('error', fallback, { once: true });
            pictureLink.append(picture);
          } else fallback();
        } else fallback();

        const copy = document.createElement('div');
        copy.className = 'fuel-hq__article-copy';
        const heading = document.createElement('h3');
        const titleLink = document.createElement('a');
        titleLink.href = url.href;
        titleLink.textContent = article.title;
        heading.append(titleLink);
        const excerpt = document.createElement('p');
        // Decode entities left in rich text by strip_html, without inserting HTML.
        const decoder = document.createElement('textarea');
        decoder.innerHTML = String(article.excerpt || '').replaceAll('<', '&lt;');
        excerpt.textContent = decoder.value;
        const readLink = document.createElement('a');
        readLink.className = 'fuel-hq__text-link';
        readLink.href = url.href;
        readLink.textContent = this.dataset.readLabel || 'Read article';
        readLink.setAttribute('aria-label', `${readLink.textContent}: ${article.title}`);
        const arrow = document.createElement('span');
        arrow.setAttribute('aria-hidden', 'true');
        arrow.textContent = '→';
        readLink.append(arrow);
        copy.append(heading, excerpt, readLink);
        card.append(pictureLink, copy);
        return card;
      }

      async loadMore(moveFocus = false) {
        if (this.loading || this.nextPage === null) return;
        this.loading = true;
        this.more.disabled = true;
        this.status.textContent = '';
        this.grid.setAttribute('aria-busy', 'true');
        const controller = new AbortController();
        this.controller = controller;
        let firstCard = null;
        try {
          // Continue past empty exact-tag pages if search returned broader matches.
          do {
            const url = new URL(this.dataset.searchUrl || '/search', location.origin);
            url.search = new URLSearchParams({
              q: 'tag:FuelHQ',
              type: 'article',
              'options[prefix]': 'none',
              view: 'fuel-hq-articles',
              page: String(this.nextPage),
            }).toString();
            const timeout = setTimeout(() => controller.abort(), 15000);
            let data;
            try {
              const response = await fetch(url.href, { credentials: 'same-origin', signal: controller.signal });
              if (!response.ok) throw new Error('Article feed unavailable');
              data = await response.json();
            } finally {
              clearTimeout(timeout);
            }
            if (!this.isConnected || controller.signal.aborted) return;
            if (!Array.isArray(data.articles) || !(data.next_page === null || (Number.isInteger(data.next_page) && data.next_page > this.nextPage))) {
              throw new Error('Invalid article feed');
            }
            const fragment = document.createDocumentFragment();
            for (const article of data.articles) {
              if (!article.id || typeof article.title !== 'string' || typeof article.url !== 'string' || this.seen.has(String(article.id))) continue;
              let card;
              try { card = this.articleCard(article); } catch { continue; }
              if (!card) continue;
              this.seen.add(String(article.id));
              firstCard ||= card;
              fragment.append(card);
            }
            this.grid.append(fragment);
            this.nextPage = data.next_page;
          } while (!firstCard && this.nextPage !== null);
          this.section.hidden = this.seen.size === 0;
          const wrapper = this.closest('[data-fuel-hq-article-block]');
          if (wrapper && this.seen.size) wrapper.hidden = false;
          this.more.hidden = this.nextPage === null;
          if (this.note) this.note.hidden = this.seen.size > 0;
          if (moveFocus && firstCard) firstCard.querySelector('h3 a')?.focus({ preventScroll: true });
        } catch {
          if (this.isConnected && this.controller === controller) {
            if (this.seen.size) {
              this.status.textContent = this.dataset.errorLabel || 'We couldn’t load more articles. Please try again.';
              this.more.hidden = false;
            } else if (this.note) {
              this.note.textContent = 'The Fuel HQ article feed could not load. Upload search.fuel-hq-articles.liquid with this section, then reload the preview.';
            }
          }
        } finally {
          if (this.controller === controller) {
            this.loading = false;
            this.more.disabled = false;
            this.grid.removeAttribute('aria-busy');
          }
        }
      }
    });
  }

  const focusFormResult = (root) => {
    const result = root.querySelector('.fuel-hq [data-fuel-hq-form-result]');
    if (result) result.focus({ preventScroll: true });
  };
  focusFormResult(document);
  if (!window.fuelHQEditorListener) {
    window.fuelHQEditorListener = true;
    document.addEventListener('shopify:section:load', (event) => focusFormResult(event.target));
  }
})();
